import { ElementState, VisibilityTrigger, ElementStyle } from '../types/experience';

/**
 * Convert legacy visibilityTriggers to a single state
 */
export function visibilityTriggersToState(
  triggers: VisibilityTrigger[] | undefined
): ElementState | null {
  if (!triggers || triggers.length === 0) return null;

  return {
    id: 'visibility-state',
    conditions: triggers.map(t => ({
      type: 'variable' as const,
      variable: t.variableName,
      value: t.value,
    })),
    style: { visible: true },
  };
}

/**
 * Get the "effective" state for an element based on variable values and current track
 * Keyframes are now handled separately via keyframeUtils
 */
export function getEffectiveState(
  states: ElementState[] | undefined,
  variableValues: Record<string, string>,
  currentTrackId?: number
): { text?: string; style?: Partial<ElementStyle> } {
  if (!states || states.length === 0) return {};

  let result: { text?: string; style?: Partial<ElementStyle> } = {};

  for (const state of states) {
    // Check if all conditions are met
    const isActive = state.conditions.length === 0 || state.conditions.every(cond => {
      if (cond.type === 'trackStart') {
        // Track start condition: current track must match
        return currentTrackId !== undefined && cond.trackId === currentTrackId;
      }
      // Variable condition (default)
      return cond.variable && variableValues[cond.variable] === cond.value;
    });

    if (isActive) {
      if (state.text !== undefined) result.text = state.text;
      if (state.style) result.style = { ...result.style, ...state.style };
    }
  }

  return result;
}

/**
 * Generate a readable label for a state based on its conditions
 */
export function getStateLabel(state: ElementState): string {
  if (state.conditions.length === 0) return 'Default';

  const parts: string[] = [];
  for (const cond of state.conditions) {
    parts.push(`${cond.variable}=${cond.value}`);
  }

  return parts.join(' + ');
}

/**
 * Create a new state with variable condition
 */
export function createVariableState(
  variableName: string,
  value: string
): ElementState {
  return {
    id: `state-var-${variableName}-${Date.now()}`,
    conditions: [{ variable: variableName, value }],
    style: { visible: true },
  };
}

/**
 * Check if a state has any variable condition
 */
export function hasVariableCondition(state: ElementState): boolean {
  return state.conditions.length > 0;
}
